import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { config } from '@/config/environment';
import { createModuleLogger } from '@/utils/logger';

const log = createModuleLogger('RELEASE_NOTES');

const FALLBACK_LOCALE = 'en';

/** A release note as shipped: one entry per language it has been written in. */
export interface RawRelease {
  version: string;
  date: string;
  locales: Record<string, { title: string; body: string }>;
}

/** A release note resolved to one language, ready to render. */
export interface ReleaseNote {
  version: string;
  date: string;
  title: string;
  /** GitHub-flavoured markdown. */
  body: string;
  /** The language actually rendered — not always the one asked for. */
  locale: string;
}

export interface ReleaseNotesState {
  releases: ReleaseNote[];
  /** The note matching the running build, if this version has one. */
  current: ReleaseNote | null;
  loading: boolean;
}

/** Tags and version labels disagree about the leading "v" — compare without it. */
function normalizeVersion(version: string): string {
  return version.trim().replace(/^v/i, '');
}

/** "sv-SE" and "sv" are the same release note. */
export function baseLanguage(language: string): string {
  return (language || FALLBACK_LOCALE).split('-')[0].toLowerCase();
}

/**
 * Pick the requested language, falling back to English so a release that has
 * not been translated yet still appears rather than silently disappearing for
 * everyone not reading English.
 */
export function resolveRelease(release: RawRelease, language: string): ReleaseNote | null {
  const wanted = baseLanguage(language);
  const locale = release.locales?.[wanted] ? wanted : FALLBACK_LOCALE;
  const content = release.locales?.[locale];
  if (!content) return null;
  return { version: release.version, date: release.date, ...content, locale };
}

export function findCurrentRelease(
  releases: ReleaseNote[],
  appVersion: string
): ReleaseNote | null {
  const wanted = normalizeVersion(appVersion);
  if (!wanted) return null;
  return releases.find((r) => normalizeVersion(r.version) === wanted) ?? null;
}

/**
 * Loads the release notes that shipped with this build.
 *
 * Generated at build time by scripts/build-release-notes.mjs and served as a
 * static file, so it needs no API, no permissions and no tenant context — it is
 * identical for everybody running this build. Every language travels in that
 * one file, so switching language re-resolves in place without a refetch.
 */
export function useReleaseNotes(): ReleaseNotesState {
  const { i18n } = useTranslation();
  const [raw, setRaw] = useState<RawRelease[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    fetch(`${import.meta.env.BASE_URL}release-notes.json`, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((payload) => {
        setRaw(Array.isArray(payload?.releases) ? payload.releases : []);
      })
      .catch((error: Error) => {
        if (error.name === 'AbortError') return;
        // Cosmetic feature — a missing or malformed file hides the section
        // rather than putting an error in front of the user.
        log.warn('Could not load release notes', { error: error.message });
        setRaw([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, []);

  const releases = useMemo(
    () =>
      raw
        .map((release) => resolveRelease(release, i18n.language))
        .filter((release): release is ReleaseNote => release !== null),
    [raw, i18n.language]
  );

  return {
    releases,
    current: findCurrentRelease(releases, config.appVersion),
    loading,
  };
}
